<script setup lang="ts">
const { t: $t } = useI18n()

  import { useWindowSize } from '@vueuse/core'
  import type { IItemMenu } from '../types'
  import { useAuthStore } from '~/stores/useAuthStore'
  import { isMobile } from '~/helpers/breakpoints'

  const emit = defineEmits<{
    (e: 'toggle', value: boolean): void
  }>()

  const props = defineProps<{
    openDashBoard: boolean
  }>()

  const { width } = useWindowSize()
  const store = useAuthStore()

  const route = useRoute()
  const colorMode = useColorMode()

  const { setPrimaryColor, setThemeColor, currentBgColor, currentColor } =
    useTheme()

  const buttons: IItemMenu[] = [
    {
      id: 1,
      label: 'Home',
      icon: 'home',
      link: '/',
    },
    {
      id: 3,
      label: 'Users',
      icon: 'users',
      link: '/users',
      value: '1',
    },
    {
      id: 5,
      label: 'Список аккаунтов',
      icon: 'user-round-pen',
      link: '/warehouses/1',
    },
    {
      id: 3,
      label: 'Settings',
      icon: 'settings',
      items: [
        {
          label: 'Create',
          icon: 'user-plus',
          link: '/users/create',
        },
        {
          label: 'Create',
          icon: 'user-plus',
          link: '/users/create',
          value: '1',
        },
      ],
    },
  ]

  const items = computed(() => [
    [
      {
        label: store.currentUser?.info.name,
        avatar: {
          src: store.currentUser?.info?.file?.url,
        },
        type: 'label',
      },
    ],
    [
      {
        label: `${$t('layouts.default.DashBoard.profile')}`,
        icon: 'i-lucide-user',
        to: '/me',
      },
      {
        label: `${$t('layouts.default.DashBoard.logout')}`,
        icon: 'i-lucide-log-out',
        kbds: ['shift', 'meta', 'q'],
        onClick: () => store.logout(),
      },
      {
        label: `${$t('layouts.default.DashBoard.color-mode')}`,
        icon: colorMode.value !== 'dark' ? 'i-lucide-sun' : 'i-lucide-moon',
        children: [
          {
            label: `${$t('layouts.default.DashBoard.light')}`,
            icon: 'i-lucide-sun',
            mode: true,
            custom: colorMode.value === 'light' ? true : false,
            onClick: () => (colorMode.preference = 'light'),
          },
          {
            label: `${$t('layouts.default.DashBoard.dark')}`,
            icon: 'i-lucide-moon',
            mode: true,
            custom: colorMode.value === 'dark' ? true : false,
            onClick: () => (colorMode.preference = 'dark'),
          },
        ],
      },
      {
        label: `${$t('layouts.default.DashBoard.system-color')}`,
        icon: 'i-lucide-palette',
        children: [
          {
            label: `${$t('layouts.default.DashBoard.select-color-text')}`,
            ring: `${currentColor.value}`,
            custom: true,
            children: [
              ...[
                'red',
                'orange',
                'amber',
                'yellow',
                'lime',
                'green',
                'emerald',
                'teal',
                'cyan',
                'sky',
                'blue',
                'indigo',
                'violet',
                'purple',
                'fuchsia',
                'pink',
                'rose',
              ].map((color) => ({
                label: `${$t(`layouts.default.DashBoard.${color}`)}`,
                ring: `${color}`,
                onClick: () => setPrimaryColor(color),
              })),
            ],
          },
          {
            label: `${$t('layouts.default.DashBoard.select-color-bg')}`,
            ring: `${currentBgColor.value}`,
            custom: true,
            children: [
              ...['stone', 'slate', 'gray', 'zinc', 'neutral'].map((color) => ({
                label: `${$t(`layouts.default.DashBoard.${color}`)}`,
                ring: `${color}`,
                onClick: () => setThemeColor(color),
              })),
            ],
          },
        ],
      },
    ],
  ])

  const toggleMobileDashBoard = () => {
    emit('toggle', !props.openDashBoard)
  }

  const sidebarClasses = computed(() => {
    const isDesktop = width.value > 640
    const isMobile = !isDesktop

    return {
      'w-[67px]': !props.openDashBoard && isDesktop,
      '!w-[290px]': props.openDashBoard && isDesktop,
      'top-0 absolute min-[100dvh] duration-500 z-[999]': isMobile,
      'left-0 w-[80%]': isMobile && props.openDashBoard,
      '-left-full w-0': isMobile && !props.openDashBoard,
    }
  })
</script>

<template>
  <div
    class="border-primary bg-default fixed flex h-screen shrink-0 flex-col gap-4 border-r px-4 duration-200 sm:relative"
    :class="sidebarClasses"
  >
    <div
      class="fixed right-2 z-90 flex h-4 w-6 transform items-center transition duration-400 will-change-transform sm:absolute sm:right-6.5 sm:bottom-16"
      :class="[openDashBoard ? '' : '', isMobile ? 'top-2' : '']"
    >
      <div
        class="flex cursor-pointer flex-row items-center gap-1.5"
        @click="emit('toggle', !openDashBoard)"
      >
        <div class="flex items-center px-1.5">
          <UIcon
            :name="
              width < 640
                ? 'i-lucide-menu'
                : openDashBoard
                  ? 'i-lucide-panel-left-close'
                  : 'i-lucide-panel-left-open'
            "
            class="size-5"
          />
        </div>
      </div>
    </div>

    <div class="flex h-full flex-col justify-between pb-5">
      <div>
        <NuxtLink to="/">
          <div
            class="flex h-16 cursor-pointer flex-row items-center gap-2 px-1.5 text-sm"
          >
            <UIcon
              v-if="openDashBoard && width < 640"
              name="i-lucide-x"
              class="size-5"
              @click.stop.prevent="toggleMobileDashBoard"
            />

            <UAvatar
              src="https://images.unsplash.com/photo-1499714608240-22fc6ad53fb2?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
              class="relative left-0 h-5 w-5"
            />

            <span v-show="openDashBoard">M2LAB</span>
          </div>
        </NuxtLink>

        <div class="flex flex-col gap-0.5 py-2">
          <MenuItem
            v-for="button in buttons"
            :key="button.id"
            variant="soft"
            :active="route.path === button.link"
            :icon="button.icon"
            :label="openDashBoard ? button.label : ''"
            :value="button?.value || ''"
            :link="button.link"
            :items="button.items"
            :open-dash-board="openDashBoard"
          >
          </MenuItem>
        </div>
      </div>

      <div>
        <UserDropDown
          :current-bg-color="currentBgColor"
          :current-color="currentColor"
          :open-dash-board="openDashBoard"
          :user="store.currentUser"
          :items="items"
        />
      </div>
    </div>
  </div>
</template>

<style scoped></style>
